<template>
    <div class="wt-container">
        <p v-if="geographic && !assessment.crop" class="introduction-text">
            Please select a crop or a crop category.
        </p>
        <p v-else-if="geographic && (assessment.crop.cropID > 10000)" class="introduction-text">
            This function does not work at the sector level. Please select a crop or a crop category.
        </p>
        <p v-else-if="geographic && (assessment.crop.cropID == 1009)" class="introduction-text">
            This function does not work for fodder crops. Please select another crop or crop category.
        </p>
        <template v-else>
            <p class="introduction-text text-center">
                The non-efficient and/or non-sustainable {{assessment.viewColor}} water footprint components of
                <span v-if="geographic">{{assessment.crop.name.toLowerCase()}} in {{assessment.getCountryOrBasinName(true)}},</span>
                <span v-else>your commodity, {{assessment.scenario.name}},</span>
                per {{countryOrBasinText}}.<br/>
                Expand table rows to view an appropriate response strategy.
            </p>
            <Table :columns="columns" :rows="rows" @open="openRow($event)" :fileName="fileName">
                <template v-slot:rowdetail="slotProps">
                    <div class="row">
                        <div class="col-sm-3">
                          <div v-if="(assessment.viewColor == 'blue') && basin">
                            <p class="fw-bold">Monthly Blue Water Availability and Footprint in the {{basin.name}} Basin</p>
                            <div class="hydrochart">
                            <HydroChart id="hydrograph"
                                        :structure="hydroData"
                                        :tooltipHeight="260"
                                        :tooltipWidth="200"
                                        :hasAxisLeft="false"
                                        :tooltipPosition="'bottom'"
                                        ></HydroChart>
                            </div>
                          </div>
                        </div>
                        <div class="col-sm-6">
                            <p class="detail-header">{{crop.name}} from {{getBasinName(slotProps.row.basinID)}} in {{country.name}}</p>
                            <div v-html="getRespondText(slotProps.row)"></div>
                        </div>
                        <div class="col-sm-3">
                          <div v-if="(assessment.viewColor == 'blue')? slotProps.row.blueWSSpot: slotProps.row.greyWSSpot">
                            <p class="fw-bold">Sectors and Crops in the {{basin.name}} Basin</p>
                            <div v-if="pieData.length > 1" class="piechart">
                            <PieChart
                                      :id="'detail-pie-chart'" :color="assessment.viewColor"
                                      :data="pieData"
                                      :attrName="attributeName"
                                      :outerRadius="70"
                                      :labelWidth="100"
                                      :labelDistance="30"
                                      :longLabel="false"
                                      >
                            </PieChart>
                            </div>
                            <div class="barchart">
                            <BarChart
                                    :id="'detail-bar-chart'"
                                    :data="barData"
                                    :labelAttr="'cropName'"
                                    :valueAttr="'percent'"
                            ></BarChart>
                            </div>
                            <p class="fw-bold">Deviations from the Benchmark in {{country.name}}</p>
                          </div>
                        </div>
                    </div>
                </template>
            </Table>
            <transition name="fade">
                <div v-if="loading" class="lottie">
                    <Lottie></Lottie>
                </div>
            </transition>
        </template>
    </div>
</template>

<script>
import HydroChart from '@/components/shared/Hydrograph.vue'
import Table from "@/components/shared/Table.vue";
import Lottie from "@/components/shared/Lottie.vue";
import PieChart from "@/components/shared/PieChart.vue";
import BarChart from "@/components/shared/BarChart";
import { assessment } from "@/services/assessment-model.js";
import { EntityLoader, countryBasinCrops, countryBasinCropsLoader, scenarioCountryBasinCrops, scenarioCountryBasinCropsLoader, getCountry, getBasin, getCrop, serverUrl } from "@/services/entity-model.js";
import { getFunctionGetCheckImage, getFunctionGetPercentageString, getFunctionGetWFString, getWFtString, getWSString } from "@/services/data-tools.js";
import * as _ from "lodash";

export default {
    name: "GeographicRespondTableCt",
    components: {
        HydroChart,
        PieChart,
        BarChart,
        Table,
        Lottie
    },
    data() {
        return {
            assessment: assessment,
            countryBasinCrops: countryBasinCrops,
            scenarioCountryBasinCrops: scenarioCountryBasinCrops,
            countryCrops: [],
            basinCrops: [],
            country: null,
            basin: null,
            crop: null
        }
    },
    mounted() {
        this.webURL = "https://waterfootprint.org/en/water-footprint/glossary/";
        
        this.countryCropsLoader = new EntityLoader(this.countryCrops, "countryCrops"),
        this.countryCropsLoader.getUrl = function(countryId) {
            return serverUrl + "nessie/entities/country-crops.jsp?format=json&cors=true&country=" + countryId;
        }
        this.basinCropsLoader = new EntityLoader(this.basinCrops, "basinCrops"),
        this.basinCropsLoader.getUrl = function(basinId) {
            return serverUrl + "nessie/entities/basin-crops.jsp?format=json&cors=true&basin=" + basinId;
        }
    },
    methods: {
        openRow: function(params) {
            this.countryCropsLoader.load(params.countryID);
            this.basinCropsLoader.load(params.basinID);
            this.country = getCountry(params.countryID);
            this.basin = getBasin(params.basinID);
            this.crop = getCrop(params.cropID);
        },
        getBasinName: function(val) {
            return !val ? "Unknown River Basins" : "the " + this.basin.name + " Basin";
        },
        getRespondText: function(row) {

            const g = this.geographic;
            const color = this.assessment.viewColor;
            const color1 = color[0].toUpperCase() + color.substr(1);
            const multiColor = color.replace("blue", "greenBlue");
            const multiColor1 = color.replace("blue", "green-blue");

            const blue = color == "blue";

            const wftSpot = row[multiColor + "WFtSpot"];
            const wft = row[multiColor + "WFt"];
            const wft20 = getWFtString(row[multiColor + "WFt20"]);
            const wftExcess = row[multiColor + "WFtExcess"];
            const wsSpot = row[color + "WSSpot"];
            const ws = row[(g? color: ("basin" + color1)) + "WS"];
            const frac = row[color + "WFFraction3"];
            const clrName0 = multiColor1;
            const clrName1 = color;
            const clrName2 = blue ? "<a href=\"" + this.webURL + "#BWS\" target=\"gloss\">blue water scarcity</a>" :
                                    "<a href=\"" + this.webURL + "#WPL\" target=\"gloss\">water pollution level</a>";
            const countryName = this.country.name;
            const basinName = this.getBasinName(row.basinID);
            const cropName = this.crop.name;

            let text = "";

            if (wftSpot) {
                text += "<p class='fw-bold'><img src='/cross.png' width='13' height='13'/> Not Efficient</p>";
                text += "<p>The " + clrName0 + " water footprint of " + cropName.toLowerCase() + " from " + countryName + " is " + wft + " and exceeds the <a href=\"" + this.webURL + "#WFB\" target=\"gloss\">global benchmark</a> by " + Math.round(wftExcess * 100) + "%. ";
                text += "The global " + clrName0 + " water footprint benchmark for " + cropName.toLowerCase() + " is " + wft20 + ".<br/>";
                text += "Response strategy: reduce water footprint through improved practices and technologies.</p>";
                text += "<p> </p>";
            } else if (wftExcess != null) {
                text += "<p class='fw-bold'><img src='/check.png' width='13' height='13'/> Efficient</p>";
                text += "<p>The " + clrName0 + " water footprint of " + cropName.toLowerCase() + " from " + countryName + " is within the <a href=\"" + this.webURL + "#WFB\" target=\"gloss\">global benchmark</a>.</p>";
                text += "<p> </p>";
            }

            if (wsSpot) {
                text += "<p class='fw-bold'><img src='/cross.png' width='13' height='13'/> Not Sustainable</p>";
                text += "<p>The annual average " + clrName2 + " in " + basinName + " is " + getWSString(ws) + ". ";
                if (blue) {
                    text += "The hydrograph on the left shows the monthly variety in the blue water scarcity.";
                }
                text += "<br/>";
                text += "Response strategy: reduce the basin water footprint through collective action.</p>";
                text += "<p> </p>";

                text += "<p>";
                if (cropName != null) {
                    if (frac >= this.threshold / 100) {
                        text += cropName + " contributes " + Math.round(frac * 100) + "% to the " + clrName1 + " water footprint of " + basinName + ". ";
                    } else {
                        text += cropName + " is a small contributor (less than " + this.threshold + "%) to the " + clrName1 + " water footprint of " + basinName + ". ";
                    }
                }
                text += "The pie chart on the right shows " + ((frac < this.threshold / 100)? cropName.toLowerCase() + " compared to ": "") + "the sectors with the largest " + clrName1 + " water footprints in " + basinName + ". (\"Other\" contains all other sectors in the basin, of less than " + this.threshold + "% each.) ";
                text += "Collective action should focus on the sectors in the pie chart. The potential water footprint gains for these sectors (insofar they concern crops) can be read from the bar chart below. The bar chart shows how their " + clrName0 + " water footprints in " + countryName + " compare to global " + clrName0 + " water footprint benchmarks.</p>";
            } else if (ws != null) {
                text += "<p class='fw-bold'><img src='/check.png' width='13' height='13'/> Sustainable</p>";
                text += "<p>The annual average " + clrName2 + " in " + basinName + " is " + getWSString(ws) + ". ";
                if (blue) {
                    text += "However, in some months it may be higher. The hydrograph on the left shows the monthly variety in the blue water scarcity. Whether the blue water footprint of " + cropName.toLowerCase() + " contributes to the monthly blue water scarcity in " + basinName + ", depends on the growing season of the " + cropName.toLowerCase() + ".";
                }

                text += "</p>";
            }
            return text;
        }
    },
    computed: {
        geographic() {
            return assessment.isGeographic();
        },
        countryOrBasinText: function() {
            if (!this.geographic) {
                return "crop, country and river basin";
            }
            const entities = [];
            if ((assessment.crop.level < 4) && (assessment.crop.cropID != 1009)) {
                entities.push("crop");
            }
            if ((assessment.assessmentType.name != "countries") || (assessment.countriesOrBasins.length > 1)) {
                entities.push("country");
            }
            if ((assessment.assessmentType.name != "basins") || (assessment.countriesOrBasins.length > 1)) {
                entities.push("river basin");
            }
            if (entities.length == 3) {
                return "crop, country and river basin";
            }
            return entities.join(" and ");
        },
        loading() {
            if (!this.geographic) {
                return scenarioCountryBasinCropsLoader.status.loading;
            }
            return countryBasinCropsLoader.status.loading;
        },
        threshold() {
            for (let threshold = 5; threshold < 10; threshold++) {
                const filteredCrops = _.filter(this.basinCrops, (o) => (o.cropID == this.crop.cropID) || (o[this.attributeName.percent] >= threshold / 100));
                if (filteredCrops.length < 5) {
                    return threshold;
                }
            }
            return 10;
        },
        rows: function() {
            const g = this.geographic;
            const color = this.assessment.viewColor;
            const color1 = color[0].toUpperCase() + color.substr(1);
            const multiColor = color.replace("blue", "greenBlue");

            return (g? this.countryBasinCrops: this.scenarioCountryBasinCrops).filter(val =>
                (val[(g? color: ("countryBasinCrop" + color1)) + "WF"] > 0) &&
                (val[multiColor + "WFt"] > 0) &&
                (val[multiColor + "WFtSpot"] || val[color + "WSSpot"])
            );
        },
        attributeName: function() {
            const viewColor = this.assessment.viewColor;
            return { percent: `${viewColor}WFFraction`, value: `${viewColor}WF`};
        },
        columns: function() {
            const g = this.geographic;
            const color = this.assessment.viewColor;
            const color1 = color[0].toUpperCase() + color.substr(1);
            const multiColor = color.replace("blue", "greenBlue");
            const multiColor1 = color1.replace("Blue", "Green-Blue");

            return [
                { key: "cropID",                                           label: "Crop",           lookupFunction: val => getCrop(val).name },
                { key: "countryID",                                        label: "Country",        lookupFunction: val => getCountry(val).name },
                { key: "basinID",                                          label: "River Basin",    lookupFunction: val => getBasin(val)?.name,          dataFunction: val => val || "(unknown basin)" },
                { key: (g ? color : ("countryBasinCrop" + color1)) + "WF", label: color1 + " WF (m³/yr)",                                                dataFunction: getFunctionGetWFString("yr") },
                { key: color + "WFFraction",                               label: "% of " + color1 + " WF in Total Selection",                           dataFunction: getFunctionGetPercentageString(1),       classes: ["left-before-row"] },
                { key: multiColor + "WFt",                                 label: multiColor1 + " WF (m³/t)",                                            dataFunction: getWFtString,                            classes: ["group", "left-row"] },
                { key: multiColor + "WFtExcess",                           label: "Deviation from Benchmark",                                            dataFunction: getFunctionGetPercentageString(-1),      classes: ["group"] },
                { key: multiColor + "WFtSpot",                             label: "Efficiency",     exportFunction: val => val ? "NOT EFFICIENT" : "",   dataFunction: getFunctionGetCheckImage("Efficient"),   classes: ["group", "right-row"] },
                { key: "whiteSpace",                                       label: "",                                                                                                                           classes: ["white-space"] },
                { key: (g ? color : ("basin" + color1)) + "WS",            label: (color == "blue") ? "Blue Water Scarcity" : "Water Pollution Level",   dataFunction: getWSString,                             classes: ["group", "left-row"] },
                { key: color + "WSSpot",                                   label: "Sustainability", exportFunction: val => val ? "NOT SUSTAINABLE" : "", dataFunction: getFunctionGetCheckImage("Sustainable"), classes: ["group", "right-row"] },
                { key: color + "WFFraction3",                              label: "% of " + color1 + " WF in River Basin by Crop",                       dataFunction: getFunctionGetPercentageString(1),       classes: ["right-after-row"] },
                { key: "details",                                          label: "Response Strategy" }
            ];
        },
        fileName() {
            if (!this.geographic) {
                return "wf-response-" + assessment.viewColor + "-" + assessment.scenario.name;
            }
            if (assessment.assessmentType.name == "crops") {
                return "wf-response-" + assessment.viewColor + "-" + assessment.crop.name;
            }
            if (assessment.countriesOrBasins.length >= 1) {
                return "wf-response-" + assessment.viewColor + assessment.countriesOrBasins.map(countryOrBasin => "-" + countryOrBasin.name) + "-" + assessment.crop.name;
            }
            return "wf-response-" + assessment.viewColor + "-the-world-" + assessment.crop.name;
        },
        pieData: function() {
            const filteredCrops = _.filter(this.basinCrops, (o) => (o.cropID == this.crop.cropID) || (o[this.attributeName.percent] >= this.threshold / 100));
            const otherCrops = _.filter(this.basinCrops, (o) => (o.cropID != this.crop.cropID) && (o[this.attributeName.percent] < this.threshold / 100));
            const data = _.map(filteredCrops, e => _.pick(e, ['cropName', this.attributeName.percent, this.attributeName.value]));
            const other = { cropName: "Other" };
            other[this.attributeName.percent] = _.sumBy(otherCrops, this.attributeName.percent);
            other[this.attributeName.value] = Math.round(_.sumBy(otherCrops, this.attributeName.value));
            data.push(other);

            return data;
        },
        barData: function () {
            const filteredBasinCrops = _.filter(this.basinCrops, (o) => (o.cropID == this.crop.cropID) || (o[this.attributeName.percent] >= this.threshold / 100));
            const filteredCountryCrops = _.intersectionWith(this.countryCrops, filteredBasinCrops, (f, s) => f.cropID === s.cropID);
            let data = _.map(filteredCountryCrops, countryCrop => {
                const c = {
                    cropName: countryCrop.cropName,
                    wftExcess: countryCrop[this.assessment.viewColor.replace("blue", "greenBlue") + "WFtExcess"]
                };
                c.percent = Math.round(c.wftExcess * 100);
                c.tooltip = `${c.cropName}<br/>${Math.abs(c.percent)}%<br/>${c.wftExcess >= 0? "above ": "below"}<br/>benchmark`;
                return c;
            });
            data = _.filter(data, i => i.wftExcess != null);

            return data;
        },
        hydroData() {
            const data = {}
            const blueAvailabilities = _.map(this.basin.blueAvailabilities.split(','), e => parseFloat(e, 10) * Math.pow(10, 6));
            const blueFootprints = _.map(this.basin.blueFootprints.split(','), e => parseFloat(e, 10) * Math.pow(10, 6));
            data["lines"] = {
                blueAvailabilities: {data: blueAvailabilities, color: 'blue', stroke: 2, label: "Blue Water Availability"},
                blueFootprints: {data: blueFootprints, color: 'black', stroke: 2, label: "Blue Water Footprint"},
            };
            data["margin"] = { top: 10, right: 10, bottom: 220, left: 10 };
            data["areas"] = [
                {top: 'blueAvailabilities', bottom: 'blueFootprints', color: 'red'},
            ];
            data["columns"] = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

            return data;
        }
    }
}
</script>
<style lang="scss" scoped>
    .hydrochart {
        height: 486px;
    }
    .detail-header {
        font-weight: 700;
        font-size: 17px;
    }
    .piechart {
        height: 300px;
    }
    .barchart {
        height: 170px;
    }
</style>
